import { Controller } from "@hotwired/stimulus"
import { refreshTrixEditors } from "../utils/refresh_trix_editors"

export default class extends Controller {
  static targets = ["bodyField", "submitButton", "titleField"]

  connect() {
    this.boundHandleCommentCreated = this.handleCommentCreated.bind(this)
    document.addEventListener("comment:created", this.boundHandleCommentCreated)

    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.element.addEventListener("keydown", this.boundHandleKeydown)

    this.boundHandleTrixInitialize = this.handleTrixInitialize.bind(this)
    this.element.addEventListener("trix-initialize", this.boundHandleTrixInitialize)

    refreshTrixEditors(this.element)
    this.element.querySelectorAll("trix-editor").forEach((editor) => {
      this.slimToolbar(editor)
      this.bindAttachPicker(editor)
    })
    this.wrapAttachments(this.element)
    this.hideMessageBodyForEdit()
  }

  disconnect() {
    document.removeEventListener("comment:created", this.boundHandleCommentCreated)
    this.element.removeEventListener("keydown", this.boundHandleKeydown)
    this.element.removeEventListener("trix-initialize", this.boundHandleTrixInitialize)
  }

  editFrame() {
    return this.element.closest("turbo-frame")
  }

  editCommentId() {
    const frameId = this.editFrame()?.id || ""
    return frameId.match(/edit_comment_(\d+)/)?.[1] || null
  }

  hideMessageBodyForEdit() {
    const commentId = this.editCommentId()
    if (!commentId) return

    const commentItem = document.querySelector(`[data-comment-id="${commentId}"]`)
    const commentBody = commentItem?.querySelector(".comment-body")
    const commentActions = commentItem?.querySelector(".comment-actions")
    if (commentBody) commentBody.style.display = "none"
    if (commentActions) commentActions.style.display = "none"
  }

  cancelEdit(event) {
    event.preventDefault()
    const commentId = this.editCommentId()
    const frame = this.editFrame()
    if (frame) frame.innerHTML = ""

    if (!commentId) return

    const commentItem = document.querySelector(`[data-comment-id="${commentId}"]`)
    const commentBody = commentItem?.querySelector(".comment-body")
    const commentActions = commentItem?.querySelector(".comment-actions")
    if (commentBody) commentBody.style.display = ""
    if (commentActions) commentActions.style.display = ""
  }

  handleTrixInitialize(event) {
    if (!this.element.contains(event.target)) return
    this.slimToolbar(event.target)
    this.bindAttachPicker(event.target)
    this.forceAttachmentsAsFiles(event.target)
    this.wrapAttachments(this.element)
  }

  // Images = pastille fichier (pas de preview inline), y compris à l’édition
  forceAttachmentsAsFiles(editorElement) {
    const attachments = editorElement.editor?.getDocument?.()?.getAttachments?.() || []
    attachments.forEach((item) => {
      const attachment = item?.setAttributes ? item : item?.attachment
      if (attachment?.hasContent?.()) return

      attachment?.setAttributes?.({ previewable: false })
    })
  }

  slimToolbar(editor) {
    const toolbar = editor.toolbarElement
    if (!toolbar || toolbar.dataset.commentsToolbarSlimmed === "true") return

    const hiddenIcons = [
      "trix-button--icon-strike",
      "trix-button--icon-heading-1",
      "trix-button--icon-quote",
      "trix-button--icon-code",
      "trix-button--icon-decrease-nesting-level",
      "trix-button--icon-increase-nesting-level",
    ]

    hiddenIcons.forEach((className) => {
      toolbar.querySelectorAll(`.${className}`).forEach((button) => button.remove())
    })

    toolbar.querySelectorAll(".trix-button-group").forEach((group) => {
      if (!group.querySelector(".trix-button")) group.remove()
    })

    toolbar.dataset.commentsToolbarSlimmed = "true"
  }

  // Trix append le file input sur document.body : hors modal Bootstrap le change
  // ne rattache souvent pas le fichier. On force un picker dans le formulaire.
  bindAttachPicker(editor) {
    const toolbar = editor.toolbarElement
    const attachButton = toolbar?.querySelector('[data-trix-action="attachFiles"]')
    if (!attachButton || attachButton.dataset.commentsAttachBound === "true") return

    attachButton.dataset.commentsAttachBound = "true"
    attachButton.addEventListener(
      "click",
      (event) => {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        this.openFilePicker(editor)
      },
      true
    )
  }

  openFilePicker(editor) {
    const existing = this.element.querySelector("[data-comments-file-picker]")
    existing?.remove()

    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.dataset.commentsFilePicker = "true"
    input.setAttribute("aria-hidden", "true")
    input.tabIndex = -1
    input.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;"

    input.addEventListener("change", () => {
      const files = input.files
      if (files?.length && editor.editor) {
        editor.editor.insertFiles(files)
      }
      input.remove()
    })

    this.element.appendChild(input)
    input.click()
  }

  onAttachmentAdd(event) {
    const attachment = event.attachment
    // Une mention « @ » (comment-mentions) est une pièce jointe de contenu, pas un fichier.
    if (attachment?.hasContent?.()) return

    attachment?.setAttributes?.({ previewable: false })

    // Bypass ActionText DirectUpload (PUT S3 côté navigateur) : upload multipart via Rails.
    if (attachment?.file && this.railsUploadUrl(event.target)) {
      event.stopPropagation()
      this.uploadAttachmentViaRails(attachment, event.target)
    }

    this.wrapAttachments(this.element)
    ;[100, 400, 1000, 2500].forEach((ms) => {
      setTimeout(() => this.wrapAttachments(this.element), ms)
    })

    this.watchAttachmentUpload(attachment)
  }

  railsUploadUrl(editorElement) {
    return editorElement?.dataset?.railsUploadUrl || null
  }

  blobUrlTemplate(editorElement) {
    return editorElement?.dataset?.blobUrlTemplate || null
  }

  uploadAttachmentViaRails(attachment, editorElement) {
    const file = attachment.file
    const uploadUrl = this.railsUploadUrl(editorElement)
    const urlTemplate = this.blobUrlTemplate(editorElement)
    if (!file || !uploadUrl || !urlTemplate) {
      attachment.remove?.()
      this.showAttachmentUploadError(attachment)
      return
    }

    const formData = new FormData()
    formData.append("file", file)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", uploadUrl, true)
    xhr.responseType = "json"
    xhr.setRequestHeader("Accept", "application/json")
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest")

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) xhr.setRequestHeader("X-CSRF-Token", csrfToken)

    xhr.upload.addEventListener("progress", (progressEvent) => {
      if (!progressEvent.lengthComputable) return
      attachment.setUploadProgress((progressEvent.loaded / progressEvent.total) * 100)
    })

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) {
        const attributes = xhr.response
        attachment.setAttributes({
          sgid: attributes.attachable_sgid,
          url: urlTemplate
            .replace(":signed_id", attributes.signed_id)
            .replace(":filename", encodeURIComponent(attributes.filename))
        })
        attachment.setUploadProgress(100)
        this.wrapAttachments(this.element)
        return
      }

      attachment.remove?.()
      this.showAttachmentUploadError(attachment)
    })

    xhr.addEventListener("error", () => {
      attachment.remove?.()
      this.showAttachmentUploadError(attachment)
    })

    xhr.send(formData)
  }

  // Filet de sécurité si l’upload reste bloqué sans sgid/url.
  watchAttachmentUpload(attachment) {
    if (!attachment?.file) return

    const startedAt = Date.now()
    let sawProgress = false
    const tick = () => {
      const attrs = attachment.getAttributes?.() || {}
      if (attrs.sgid || attrs.url) {
        this.wrapAttachments(this.element)
        return
      }

      const progress = attachment.uploadProgress || 0
      if (progress > 0) sawProgress = true

      const elapsed = Date.now() - startedAt
      const failed =
        (!sawProgress && elapsed >= 12000) ||
        (sawProgress && elapsed >= 120000)

      if (failed) {
        attachment.remove?.()
        this.showAttachmentUploadError(attachment)
        return
      }

      setTimeout(tick, 500)
    }

    setTimeout(tick, 500)
  }

  showAttachmentUploadError(attachment) {
    const filename = attachment?.file?.name || attachment?.getAttributes?.()?.filename || "fichier"
    const message = `Échec de l’envoi de « ${filename} ». Réessayez dans un instant.`

    if (window.alert) window.alert(message)
  }

  wrapAttachments(container) {
    container.querySelectorAll("figure.attachment").forEach((figure) => {
      this.makeFigureAttachmentClickable(figure)
    })

    container.querySelectorAll("action-text-attachment[url]").forEach((el) => {
      const url = el.getAttribute("url")
      if (!url) return

      const caption = el.querySelector("figcaption")
      if (!caption || caption.closest("a")) return

      const link = this.buildAttachmentLink(url)
      caption.parentNode.insertBefore(link, caption)
      link.appendChild(caption)
    })
  }

  makeFigureAttachmentClickable(figure) {
    const url = this.attachmentUrlFromFigure(figure)
    if (!url) return

    let link = figure.querySelector(":scope > a[href], :scope a.attachment__link, figcaption a[href]")
    if (!link) {
      const caption = figure.querySelector("figcaption")
      if (!caption || caption.closest("a")) return

      link = this.buildAttachmentLink(url)
      caption.parentNode.insertBefore(link, caption)
      link.appendChild(caption)
    } else {
      link.href = url
      link.target = "_blank"
      link.rel = "noopener noreferrer"
    }

    if (link.dataset.commentsOpenBound === "true") return
    link.dataset.commentsOpenBound = "true"
    // contenteditable empêche la navigation native sur <a>
    link.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      window.open(link.href, "_blank", "noopener,noreferrer")
    })
  }

  attachmentUrlFromFigure(figure) {
    const existing = figure.querySelector("a[href]")?.getAttribute("href")
    if (existing) return existing

    try {
      const data = JSON.parse(figure.dataset.trixAttachment || "{}")
      return data.href || data.url || null
    } catch (_) {
      return null
    }
  }

  buildAttachmentLink(url) {
    const link = document.createElement("a")
    link.href = url
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    link.className = "attachment__link"
    return link
  }

  handleCommentCreated() {
    this.resetForm()
  }

  onFocus() {}

  handleKeydown(event) {
    // Enter dans le dialogue URL soumettrait le formulaire (création) :
    // on bloque le submit natif, Trix gère déjà la confirmation sur l’input.
    if (event.target.closest(".trix-dialogs, [data-trix-dialog]")) {
      if (event.key === "Enter") event.preventDefault()
      return
    }
    if (!event.target.closest("trix-editor")) return

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.submitForm()
    }
  }

  submitForm() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.click()
    }
  }

  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.resetForm()
    }
  }

  resetForm() {
    const trixEditor = this.element.querySelector("trix-editor")
    if (trixEditor?.editor) {
      trixEditor.editor.loadHTML("")
    }

    if (this.hasTitleFieldTarget) {
      this.titleFieldTarget.value = ""
    }
  }
}
